import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import {Backend, PERMISSION} from '@generated-backend-types';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {TableComponent} from '../../../../../components/table/table.component';
import {StrictFormGroup} from '@browsepedia/ng-forms';
import {NonNullableFormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {
  MultiSelectWithServiceListComponent
} from '../../../../../components/form-inputs/multi-select-with-service-list/multi-select-with-service-list.component';
import {AddableProcessesService} from '../../../../../services/api/quality-management/addable-processes.service';
import {SubProcessService} from '../../../../../services/api/quality-management/sub-process.service';
import {Subject, take, takeUntil} from 'rxjs';
import {UserSessionService} from '../../../../../services/user-session.service';
import {TableFunctionList} from '../../../../../entities/table-function-list.entity';
import {CustomTableFunctionTypeEnum} from '../../../../../enums/CustomTableFunctionTypeEnum';
import {BsModalService, ModalModule} from 'ngx-bootstrap/modal';
import {
  ConfirmDeleteModalComponent
} from '../../../../../components/confirm-delete-modal/confirm-delete-modal.component';
import {FormSubmitService} from '../../../../../services/form-submit.service';
import Process = Backend.App.Resources.Dashboard.QualityManagement.Process;
import SubProcess = Backend.App.Resources.Dashboard.QualityManagement.SubProcess;
import SubProcessPivotData = Backend.App.Data.Dashboard.QualityManagement.SubProcessPivotData;
import ProcessVersion = Backend.App.Resources.Dashboard.QualityManagement.ProcessVersion;

@Component({
  selector: 'app-authenticated-quality-management-processes-sub-processes',
  templateUrl: './sub-processes.component.html',
  imports: [
    TranslatePipe,
    TableComponent,
    ReactiveFormsModule,
    MultiSelectWithServiceListComponent,
    ModalModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class SubProcessesComponent implements OnInit, OnDestroy {
  @Input({required: true}) public process!: Process;
  @Input() public processVersion?: ProcessVersion;
  public reloadTable$ = new EventEmitter<void>();
  public isAddingSubProcess = false;
  public form: StrictFormGroup<SubProcessPivotData> = this.fb.group({
    sub_process_id: this.fb.control('', [Validators.required]),
  });
  public showFunctionList: TableFunctionList = {
    custom: rowData => {
      return {
        type: CustomTableFunctionTypeEnum.LINK,
        payload: '/quality-management/processes/' + (rowData as SubProcess).process_id + '/current',
        value: (rowData as SubProcess).process_ext_id ?? undefined,
      };
    }
  }
  protected onDestroy$: Subject<void> = new Subject<void>();
  public editFunctionList: TableFunctionList = {
    delete: rowData => {
      if (this.processVersion !== undefined) return;
      const subProcessToDelete = rowData as SubProcess;

      this.bsModalService.show(ConfirmDeleteModalComponent, {
        initialState: {
          modelId: subProcessToDelete.id,
          dialogTranslation: this.translate.instant('authenticated.quality-management.processes.edit.sub_processes.delete.confirm_title', {itemName: subProcessToDelete.process_title + ' ' + subProcessToDelete.process_ext_id}),
          successTranslation: this.translate.instant('authenticated.quality-management.processes.edit.sub_processes.delete.success'),
          errorTranslation: this.translate.instant('authenticated.quality-management.processes.edit.sub_processes.delete.error'),
          permissions: [PERMISSION.QUALITYMANAGEMENT_SUBPROCESSPIVOT_DELETE_ALL],
          observable: this.subProcessService.delete(subProcessToDelete.id),
        }
      }).content?.deleteSuccess.pipe(
        takeUntil(this.onDestroy$),
        take(1)
      ).subscribe(() => {
        this.reloadTable$.emit();
        this.cdr.markForCheck();
      });
    },
    custom: rowData => {
      return {
        type: CustomTableFunctionTypeEnum.LINK,
        payload: '/quality-management/processes/' + (rowData as SubProcess).process_id + '/current',
        value: (rowData as SubProcess).process_ext_id ?? undefined,
      };
    }
  }

  public constructor(
    private readonly fb: NonNullableFormBuilder,
    protected readonly userSessionService: UserSessionService,
    protected readonly addableProcessesService: AddableProcessesService,
    private readonly subProcessService: SubProcessService,
    private readonly translate: TranslateService,
    private readonly cdr: ChangeDetectorRef,
    private readonly bsModalService: BsModalService,
    private readonly formSubmitService: FormSubmitService,
  ) {
  }

  public ngOnInit() {
    this.addableProcessesService.forProcess(this.process);
    this.subProcessService.forProcess(this.process);
  }

  public cancelAddingSubProcess() {
    this.form.setValue({sub_process_id: ''});
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.isAddingSubProcess = false;
    this.cdr.markForCheck();
  }

  public addSubProcess() {
    if (this.processVersion !== undefined) return;

    this.formSubmitService.submit<SubProcess, SubProcessPivotData, SubProcessService>({
      form: this.form,
      service: this.subProcessService,
      createPermission: PERMISSION.QUALITYMANAGEMENT_SUBPROCESSPIVOT_CREATE_ALL,
      createSuccessMessage: this.translate.instant('authenticated.quality-management.processes.create.success'),
      createErrorMessage: this.translate.instant('authenticated.quality-management.processes.create.error'),
    }).subscribe(subProcess => {
      if (!subProcess) return;

      this.cancelAddingSubProcess();
      this.reloadTable$.emit();
      this.cdr.markForCheck();
    });
  }

  public ngOnDestroy() {
    this.onDestroy$.next();
  }
}
